import { useState, useEffect, useCallback, useContext } from "react";
import WeightInput from "components/weight-input";
import WeightEstimation from "components/weight-estimation";
import { AnimatePresence } from "framer-motion";
import FormContainer from "components/form-container";
import { useLocation, useOutletContext } from "react-router-dom";
import ProgressBar from "components/progress-bar";
import { calculateEstimate } from "lib/calculateEstimate";
import {
  localizeDecimalSeparator,
  getQueryParams,
  checkIfFullKiosk,
} from "lib/utils";
import { useTranslation } from "react-i18next";
import weightRestrictions from "consts/weight-restrictions.json";
import InputValidation from "components/input-validation";
import { TAKEBACK_METHOD_NAME } from "consts/query-params";
import { useCurrentLocation } from "lib/utils";
import useWhen from "hooks/useWhen";
import { PAYMENT_ARGS } from "consts/feature-config";
import RewardOverview from "components/reward-overview";
import stores from "consts/stores.json";
import AuthContext from "contexts/AuthContext";
import FloodlightEvent from "components/floodlight-event";
import { EVENT_8 } from "utils/tracking";

export default function WeightPage() {
  const [weightInput, setWeightInput] = useState(null);
  const [skipMethodPage, setSkipMethodPage] = useState(false);
  const [weightAlarm, setWeightAlarm] = useState(null);

  // translation
  const { t } = useTranslation();

  // Get query params
  const location = useLocation();
  const queryParams = getQueryParams(location.search);

  const unit = `${t("scale.weight")}`;

  // contitional navigation based on location
  const { location: currentLocation } = useCurrentLocation();

  const currentStore = currentLocation?.takebackMethod?.find((method) =>
    method.startsWith("in_store")
  )
    ? stores.find(
        (store) =>
          store.value ===
          currentLocation.takebackMethod.find((method) =>
            method.startsWith("in_store")
          )
      )
    : null;

  const maxWeight =
    currentStore && currentStore?.return_type === "kiosk"
      ? weightRestrictions.find((weight) => weight.unit === unit).maxKiosk
      : currentStore && currentStore?.return_type === "drop_off"
        ? weightRestrictions.find((weight) => weight.unit === unit).maxDropOff
        : weightRestrictions.find((weight) => weight.unit === unit).maxMail;

  useEffect(() => {
    if (
      currentLocation?.takebackMethod?.includes("mail") &&
      currentLocation?.takebackMethod?.length === 1
    ) {
      setSkipMethodPage(true);
    } else {
      setSkipMethodPage(false);
    }
  }, [queryParams, currentLocation]);

  const destination = skipMethodPage
    ? `${t("urls.trade_in_checklist")}`
    : `${t("urls.trade_in_method")}`;

  // Set right side visual
  const { setSidebarVisual } = useOutletContext();
  useEffect(() => {
    setSidebarVisual(currentLocation.locale === "de-DE" ? "euro" : "dollar");
  }, [currentLocation, setSidebarVisual]);

  //handle input change and max weight
  // const isStateExempt =
  //   currentLocation.state === "CO" || currentLocation.state === "GA";

  const checkWeightLimits = useCallback(
    (weight) => {
      if (maxWeight && weight > maxWeight) {
        setWeightAlarm(
          t("ui.weight_alarm_max", {
            maxWeight: maxWeight,
            unit: unit,
          })
        );
      } else {
        setWeightAlarm(null);
      }
    },
    [t, maxWeight, unit]
  );

  const { user } = useContext(AuthContext);

  // Initialize input with query param
  useEffect(() => {
    if (queryParams.w) {
      checkWeightLimits(parseFloat(queryParams.w));
      setWeightInput(queryParams.w);
      setWeightAlarm(null);
    }
  }, [queryParams.w, checkWeightLimits]);

  const handleInputChange = (event) => {
    const newValue = event.target.value;
    const newNumericValue = newValue.replace(/,/g, ".");
    setWeightInput(newValue);
    checkWeightLimits(parseFloat(newNumericValue));
  };

  const formattedWeight =
    currentLocation?.locale !== "en-US"
      ? weightInput?.toString().replace(",", ".")
      : weightInput;

  const parsedWeight = parseFloat(formattedWeight).toFixed(2);
  const weightEstimate =
    currentLocation?.locale !== "en-US"
      ? parsedWeight.toString().replace(".", ",")
      : parsedWeight;

  const estimate = calculateEstimate(formattedWeight);

  // Generate updated params
  const updatedParams = { ...queryParams, w: formattedWeight };
  if (skipMethodPage) {
    updatedParams[TAKEBACK_METHOD_NAME] = "mail";
  }

  //only show cash payment in appropriate environments
  const showPayment = useWhen(PAYMENT_ARGS);

  const isKiosk = checkIfFullKiosk(currentLocation);

  return (
    <FormContainer
      submitLabel={t("ui.button_continue")}
      submitHref={destination}
      submitParams={updatedParams}
      submitDisabled={!parseFloat(weightInput) || weightAlarm}
      submitTracking={[EVENT_8]}
      secondaryLabel={t("ui.button_cancel")}
      secondaryHref="/"
      tertiaryLabel={
        !user ? (
          <>
            <span className="md:hidden">Skip</span>
            <span className="hidden md:inline">Skip to Login</span>
          </>
        ) : null
      }
      tertiaryHref={`${t("urls.trade_in_email")}`}
      tertiaryParams={{ signUpOnly: true }}
    >
      <FloodlightEvent tag="DC-14832541/pageview/pl_weigh+per_session" />
      <ProgressBar
        step={1}
        showLabel
        stepsNum={
          currentLocation?.state === null
            ? user
              ? 5
              : 6
            : isKiosk
              ? user
                ? 5
                : 6
              : user
                ? 5
                : 6
        }
      />
      <div className="flex flex-col gap-regular">
        <h2 className="text-primary text-6xl">
          Enter the weight of the LEGO® bricks you're trading in
        </h2>
        <p className="text-tertiary text-2xl">
          Place your LEGO bricks on a scale and enter the weight below to get an
          instant estimate of their value.
        </p>
      </div>
      <div>
        <WeightInput
          caption={t("ui.input_weight")}
          placeholder="0.0"
          unit={unit}
          className="max-w-6xl sm:max-w-64"
          value={weightInput}
          onChange={handleInputChange}
          locale={currentLocation?.locale}
        />
        {weightAlarm && <InputValidation errorMessage={weightAlarm} />}
        {isKiosk && weightInput > 25 && (
          <InputValidation
            errorMessage={`Your estimated weight is over the ${weightRestrictions
              .find((weight) => weight.unit === unit)
              .maxMail.toFixed(
                2
              )} lbs Mail-In limit. You can either create multiple trade-ins to meet the weight criteria or choose the in-store option in the next step.`}
          />
        )}
        <div className="mt-4 text-tertiary text-xs">
          {t("trade-in.weight.footnote")}
        </div>
      </div>

      {!isKiosk && !parseFloat(weightInput) && (
        <div className="pb-4 border-b border-[#e4e4e4]">
          <h3 className="font-bold flex items-center mb-1">
            <span className="material-symbols-outlined scale-75 mr-1">
              scale
            </span>
            Trade-In weight limit: 25.00 lbs
          </h3>
          <div className="mt-0 text-tertiary text-xs ">
            {t("trade-in.weight.reason")}
          </div>
        </div>
      )}

      {isKiosk && !parseFloat(weightInput) && (
        <div className="pb-4 border-b border-[#e4e4e4]">
          <h3 className="font-bold flex items-center mb-1">
            <span className="material-symbols-outlined scale-75 mr-1">
              scale
            </span>
            Trade-In weight limits:
          </h3>
          <div className="flex items-center gap-2 mt-3">
            <span className="material-symbols-outlined scale-90">
              package_2
            </span>
            Mail-in:{" "}
            <span className="font-bold">
              {weightRestrictions
                .find((weight) => weight.unit === unit)
                .maxMail.toFixed(2)}{" "}
              lbs
            </span>
            <span className="material-symbols-outlined scale-90 ml-3">
              storefront
            </span>
            In-Store:{" "}
            <span className="font-bold">
              {weightRestrictions
                .find((weight) => weight.unit === unit)
                .maxKiosk.toFixed(2)}{" "}
              lbs
            </span>
          </div>
          <div className="mt-0 text-tertiary text-xs ">
            {t("trade-in.weight.reason")}
          </div>
        </div>
      )}

      <AnimatePresence>
        {parseFloat(weightInput) && (
          <WeightEstimation
            className="mt-4"
            title={"Your reward will be worth"}
            singleValue={
              !showPayment
                ? localizeDecimalSeparator(estimate.gift, currentLocation)
                : null
            }
            range={
              showPayment
                ? [
                    localizeDecimalSeparator(estimate.payment, currentLocation),
                    localizeDecimalSeparator(estimate.gift, currentLocation),
                    currentLocation,
                  ]
                : null
            }
            animate
            locale={currentLocation?.locale}
            description={t("ui.estimate_part2", {
              weight: weightEstimate,
              unit: unit,
            })}
            minMaxReached={weightAlarm}
            visual="giftcard"
          />
        )}
        {!parseFloat(weightInput) && (
          <RewardOverview queryParams={queryParams} />
        )}
      </AnimatePresence>
    </FormContainer>
  );
}
